/**
 * DecisionFlow — PharmaManager
 * ui/src/components/tenant-admin/PharmaManager.jsx
 *
 * Domain: §P4-13 Pharma Script Quotas
 * Nav route: /admin/pharma
 * API:
 *   GET  /api/v1/pharma-script-quotas?fiscal_period_id={id}
 *   POST /api/v1/pharma-script-quotas
 *   POST /api/v1/pharma-script-quotas/{id}/approve
 * Feature gate: Enterprise — pharma_script
 *   403 FEATURE_UNAVAILABLE → GateStatusBanner.
 * territory_id scoped (no plan_component_id).
 * rate_per_script on quota row.
 * State derived from approved_by / submitted_at — no status column.
 * Path: ui/src/components/tenant-admin/PharmaManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  territory_id:   '',
  product_code:   '',
  quota_scripts:  '',
  rate_per_script: '',
  effective_date:  '',
  expiration_date: '',
};

// ---------------------------------------------------------------------------
// PharmaManager
// ---------------------------------------------------------------------------

export default function PharmaManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.pharma')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }
  const fpId = period?.fiscal_period_id ?? fiscalPeriodId ?? null;

  const [gated, setGated]                   = useState(false);
  const [items, setItems]                   = useState([]);
  const [listLoading, setListLoading]       = useState(false);
  const [listError, setListError]           = useState('');
  const [selectedItem, setSelectedItem]     = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [saving, setSaving]                 = useState(false);
  const [formError, setFormError]           = useState('');
  const [actionLoading, setActionLoading]   = useState(false);
  const [actionError, setActionError]       = useState('');
  const [toast, setToast]                   = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // ---------------------------------------------------------------------------
  // Load — enterprise gate check first
  // ---------------------------------------------------------------------------

  const loadItems = useCallback(async () => {
    if (!user) return;
    setListLoading(true);
    setListError('');
    setGated(false);
    try {
      const url = fpId
        ? `/api/v1/pharma-script-quotas?fiscal_period_id=${fpId}`
        : '/api/v1/pharma-script-quotas';
      const data = await apiGet(url);
      setItems(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'FEATURE_UNAVAILABLE') {
        setGated(true);
      } else {
        setListError(getErrorMessage(error, 'Unable to load pharma script quotas.'));
      }
    } finally {
      setListLoading(false);
    }
  }, [user, fpId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ---------------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------------

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.territory_id.trim() || !form.rate_per_script) {
      setFormError('Territory ID and rate per script are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await apiPost('/api/v1/pharma-script-quotas', {
        territory_id:      form.territory_id.trim(),
        fiscal_period_id:  fpId,
        product_code:      form.product_code.trim() || null,
        quota_scripts:     form.quota_scripts ? parseInt(form.quota_scripts, 10) : null,
        rate_per_script:   parseFloat(form.rate_per_script),
        effective_date:    form.effective_date || null,
        expiration_date:   form.expiration_date || null,
      });
      setShowCreateForm(false);
      setForm(EMPTY_FORM);
      showToast('Pharma script quota created.');
      await loadItems();
    } catch (error) {
      setFormError(getErrorMessage(error, 'Failed to create pharma script quota.'));
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Submit / Approve
  // ---------------------------------------------------------------------------

  async function handleSubmit() {
    if (!selectedItem) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/pharma-script-quotas/${selectedItem.pharma_script_quota_id}/submit`);
      showToast('Submitted for approval.');
      await loadItems();
      setSelectedItem((prev) => ({ ...prev, submitted_at: new Date().toISOString() }));
    } catch (error) {
      setActionError(getErrorMessage(error, 'Failed to submit.'));
    } finally {
      setActionLoading(false);
    }
  }

  async function handleApprove() {
    if (!selectedItem) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/pharma-script-quotas/${selectedItem.pharma_script_quota_id}/approve`);
      showToast('Pharma script quota approved.');
      await loadItems();
      setSelectedItem((prev) => ({ ...prev, approved_by: user?.user_id || user?.id }));
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_NOT_ALLOWED') {
        setActionError('Self-approval is not permitted (NN-10).');
      } else {
        setActionError(getErrorMessage(error, 'Failed to approve.'));
      }
    } finally {
      setActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) {
    return (
      <section className="flex flex-col gap-4">
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  if (gated) {
    return (
      <section className="flex flex-col gap-4">
        <GateStatusBanner feature="pharma_script" />
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Detail view
  // ---------------------------------------------------------------------------

  if (selectedItem) {
    const state = deriveState(selectedItem);
    return (
      <section className="flex flex-col gap-4">
        <button type="button" onClick={() => { setSelectedItem(null); setActionError(''); }} className={btnSecondary + ' w-fit'}>
          ← Back to list
        </button>

        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
              Territory {selectedItem.territory_id}
              {selectedItem.product_code ? ` — ${selectedItem.product_code}` : ''}
            </h2>
            <StatusPill status={state} />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Rate / Script</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.rate_per_script != null ? Number(selectedItem.rate_per_script).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Quota Scripts</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.quota_scripts != null ? Number(selectedItem.quota_scripts).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{selectedItem.effective_date ?? '—'}</p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Expires</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{selectedItem.expiration_date ?? '—'}</p>
            </div>
          </div>
          {actionError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
              {actionError}
            </div>
          )}
        </div>

        {!readOnly && (
          <ApprovalPanel
            variant="pharma_script_quota"
            currentState={state}
            submittedBy={selectedItem.submitted_by}
            currentUserId={currentUserId}
            onSubmit={state === 'draft' ? handleSubmit : null}
            onApprove={state === 'pending_approval' ? handleApprove : null}
            onReject={null}
            approvedBy={selectedItem.approved_by}
            approvedAt={selectedItem.approved_at}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // List view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">Pharma Script</h1>

      {!fpId && (
        <div className="rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
          No active fiscal period. Pharma script quotas require a period.
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Script Quotas</h2>
        {!readOnly && (
          <button
            type="button"
            onClick={() => { setShowCreateForm((v) => !v); setFormError(''); setForm(EMPTY_FORM); }}
            className={btnPrimary}
          >
            {showCreateForm ? 'Cancel' : '+ Add Quota'}
          </button>
        )}
      </div>

      {showCreateForm && (
        <form
          onSubmit={handleCreate}
          className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3"
        >
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Pharma Script Quota</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className={labelClass}>
              Territory ID <span className="text-[rgb(var(--color-danger))]">*</span>
              <input type="text" value={form.territory_id} onChange={(e) => setForm((f) => ({ ...f, territory_id: e.target.value }))} placeholder="e.g. TERR-001" className={inputClass} required />
            </label>
            <label className={labelClass}>
              Product Code
              <input type="text" value={form.product_code} onChange={(e) => setForm((f) => ({ ...f, product_code: e.target.value }))} placeholder="e.g. PROD-A" className={inputClass} />
            </label>
            <label className={labelClass}>
              Rate / Script <span className="text-[rgb(var(--color-danger))]">*</span>
              <input type="number" step="0.0001" min="0" value={form.rate_per_script} onChange={(e) => setForm((f) => ({ ...f, rate_per_script: e.target.value }))} placeholder="0.00" className={inputClass} required />
            </label>
            <label className={labelClass}>
              Quota Scripts
              <input type="number" step="1" min="0" value={form.quota_scripts} onChange={(e) => setForm((f) => ({ ...f, quota_scripts: e.target.value }))} placeholder="0" className={inputClass} />
            </label>
            <label className={labelClass}>
              Effective Date
              <input type="date" value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} className={inputClass} />
            </label>
            <label className={labelClass}>
              Expiration Date
              <input type="date" value={form.expiration_date} onChange={(e) => setForm((f) => ({ ...f, expiration_date: e.target.value }))} className={inputClass} />
            </label>
          </div>
          {formError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{formError}</div>
          )}
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Create Quota'}</button>
            <button type="button" onClick={() => { setShowCreateForm(false); setFormError(''); }} className={btnSecondary}>Cancel</button>
          </div>
        </form>
      )}

      {listLoading && (
        <div className="animate-pulse space-y-2">
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
        </div>
      )}
      {listError && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{listError}</div>
      )}
      {!listLoading && !listError && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No pharma script quotas</p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Add quotas to configure script-based compensation by territory.</p>
        </div>
      )}
      {items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th className={thClass}>Territory</th>
                <th className={thClass}>Product</th>
                <th className={thClass}>Rate / Script</th>
                <th className={thClass}>Quota Scripts</th>
                <th className={thClass}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.pharma_script_quota_id}
                  className="cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
                  onClick={() => { setSelectedItem(item); setActionError(''); }}
                >
                  <td className={tdClass + ' font-medium'}>{item.territory_id}</td>
                  <td className={tdClass}>{item.product_code ?? '—'}</td>
                  <td className={tdClass}>{item.rate_per_script != null ? Number(item.rate_per_script).toLocaleString() : '—'}</td>
                  <td className={tdClass}>{item.quota_scripts != null ? Number(item.quota_scripts).toLocaleString() : '—'}</td>
                  <td className={tdClass}><StatusPill status={deriveState(item)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg">
          {toast}
        </div>
      )}
    </section>
  );
}
